/**
 * SQL Storage Adapter
 *
 * Remote SQL database implementation of IStorageAdapter via BFF REST API.
 * Communicates with the BFF server for persistent, multi-user storage.
 *
 * Features:
 * - Auth0 JWT authentication
 * - Automatic retry with exponential backoff
 * - Optimistic locking with server-side conflict detection
 * - Multi-user support with organization filtering
 * - Offline detection and error handling
 *
 * API Endpoints:
 * - GET /api/storage/:type (list)
 * - GET /api/storage/:type/:id (get)
 * - POST /api/storage/:type (create)
 * - PUT /api/storage/:type/:id (update)
 * - DELETE /api/storage/:type/:id (delete)
 *
 * @example
 * ```ts
 * const adapter = new SqlStorageAdapter<SurveyData>('survey', authService);
 * const result = await adapter.save('survey-123', data, { expectedVersion: 1 });
 * ```
 */

import type {
  IStorageAdapter,
  Result,
  StoredData,
  StorageMetadata,
  SaveOptions,
  ListOptions,
  ConflictInfo,
} from './IStorageAdapter'
import { StorageError, StorageErrorCode } from './IStorageAdapter'
import type { AuthService } from './factory'
import { HttpClient, type RetryConfig } from './HttpClient'

export class SqlStorageAdapter<T> implements IStorageAdapter<T> {
  readonly type = 'sql' as const
  private readonly storageType: string
  private readonly httpClient: HttpClient

  /**
   * Create a new SqlStorageAdapter
   *
   * @param storageType Type of storage (e.g., 'survey', 'site', 'assumptions')
   * @param authService Auth0 service for JWT tokens
   * @param baseUrl Base URL for BFF server (defaults to env variable or /api)
   * @param retryConfig Retry configuration (optional)
   */
  constructor(
    storageType: string,
    authService: AuthService,
    baseUrl?: string,
    retryConfig?: Partial<RetryConfig>
  ) {
    this.storageType = storageType
    const url = baseUrl || import.meta.env.VITE_BFF_URL || '/api'
    this.httpClient = new HttpClient(authService, url, retryConfig)
  }

  async get(key: string): Promise<Result<StoredData<T> | null>> {
    try {
      const headers = await this.httpClient.getAuthHeaders()
      const url = `${this.httpClient.getBaseUrl()}/storage/${this.storageType}/${encodeURIComponent(key)}`

      // API returns the entity directly with: { id, userId, surveyId, surveyName, ...entityFields, version, createdAt, updatedAt }
      const response = await this.httpClient.fetch<
        T & {
          id: string
          userId?: string
          version: number
          createdAt: string
          updatedAt: string
        }
      >(url, { method: 'GET', headers })

      // Extract metadata fields and create the data object
      // Note: createdAt is destructured but not used (we only need updatedAt for lastModified)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { id, userId, version, createdAt, updatedAt, ...dataFields } = response

      return {
        success: true,
        data: {
          data: dataFields as T,
          metadata: {
            id,
            version,
            lastModified: updatedAt,
            ...(userId && { modifiedBy: userId }),
          },
        },
      }
    } catch (error) {
      if (error instanceof StorageError && error.code === StorageErrorCode.NOT_FOUND) {
        // Not found is not an error for get() - return null
        return { success: true, data: null }
      }

      return {
        success: false,
        error:
          error instanceof StorageError
            ? error
            : new StorageError('Failed to get data', StorageErrorCode.UNKNOWN, error),
      }
    }
  }

  async save(
    key: string,
    data: T,
    options: SaveOptions = {}
  ): Promise<Result<StoredData<T>, ConflictInfo<T>>> {
    try {
      const headers = await this.httpClient.getAuthHeaders()

      // Check if item exists to determine POST vs PUT
      const existsResult = await this.exists(key)
      const isUpdate = existsResult.success && existsResult.data

      const baseUrl = this.httpClient.getBaseUrl()
      // Always include the key (surveyId) in the URL for both POST and PUT
      const url = `${baseUrl}/storage/${this.storageType}/${encodeURIComponent(key)}`

      const method = isUpdate ? 'PUT' : 'POST'

      // Transform frontend data to match backend expected input types
      // Backend expects specific input types, not the full frontend types with metadata
      let backendData: unknown = data
      if (this.storageType === 'assumptions') {
        // Backend expects: { surveyName, equipmentPortCounts }
        // Frontend sends: full SurveyAssumptions with surveyId, createdAt, updatedAt, version
        backendData = {
          surveyName: (data as any).surveyName,
          equipmentPortCounts: (data as any).equipmentPortCounts,
        }
      } else if (this.storageType === 'master-equipment') {
        // Backend expects: { surveyName, equipmentConfigs }
        // Frontend sends: full SurveyMasterEquipmentSettings with surveyId, createdAt, updatedAt, version
        backendData = {
          surveyName: (data as any).surveyName,
          equipmentConfigs: (data as any).equipmentConfigs,
        }
      } else if (this.storageType === 'port-mappings') {
        // Backend expects: { surveyName, templates }
        // Frontend sends: SurveyPortMapping with surveyId, activeTemplateId, templates
        // Note: surveyId is used as surveyName if surveyName not available
        backendData = {
          surveyName: (data as any).surveyName || (data as any).surveyId || key,
          templates: (data as any).templates || [],
        }
      }

      const body = {
        id: key,
        data: backendData,
        version: options.expectedVersion,
        userId: options.userId,
        orgId: options.orgId,
      }

      // API returns: { id, data, version, lastModified, modifiedBy?, orgId? }
      const response = await this.httpClient.fetch<{
        id: string
        data: T
        version: number
        lastModified: string
        modifiedBy?: string
        orgId?: string
      }>(url, {
        method,
        headers,
        body: JSON.stringify(body),
      })

      return {
        success: true,
        data: {
          data: response.data,
          metadata: {
            id: response.id,
            version: response.version,
            lastModified: response.lastModified,
            ...(response.modifiedBy && { modifiedBy: response.modifiedBy }),
            ...(response.orgId && { orgId: response.orgId }),
          },
        },
      }
    } catch (error) {
      // Handle conflict error
      if (error instanceof StorageError && error.code === StorageErrorCode.CONFLICT) {
        const conflictData = error.cause as {
          currentVersion: number
          currentData: T
        }

        const conflict: ConflictInfo<T> = {
          currentVersion: conflictData.currentVersion,
          attemptedVersion: options.expectedVersion || 0,
          currentData: conflictData.currentData,
          attemptedData: data,
        }

        return { success: false, error: conflict }
      }

      throw error instanceof StorageError
        ? error
        : new StorageError('Failed to save data', StorageErrorCode.UNKNOWN, error)
    }
  }

  async delete(key: string, options: SaveOptions = {}): Promise<Result<void, ConflictInfo<T>>> {
    try {
      const headers = await this.httpClient.getAuthHeaders()
      const baseUrl = this.httpClient.getBaseUrl()
      const url = `${baseUrl}/storage/${this.storageType}/${encodeURIComponent(key)}`

      const urlWithVersion =
        options.expectedVersion !== undefined ? `${url}?version=${options.expectedVersion}` : url

      await this.httpClient.fetch<void>(urlWithVersion, { method: 'DELETE', headers })

      return { success: true, data: undefined }
    } catch (error) {
      // Handle conflict error
      if (error instanceof StorageError && error.code === StorageErrorCode.CONFLICT) {
        const conflictData = error.cause as {
          currentVersion: number
          currentData: T
        }

        const conflict: ConflictInfo<T> = {
          currentVersion: conflictData.currentVersion,
          attemptedVersion: options.expectedVersion || 0,
          currentData: conflictData.currentData,
          attemptedData: conflictData.currentData,
        }

        return { success: false, error: conflict }
      }

      // Handle NOT_FOUND as error result, not exception
      if (error instanceof StorageError && error.code === StorageErrorCode.NOT_FOUND) {
        return { success: false, error: {} as ConflictInfo<T> }
      }

      return {
        success: false,
        error: {} as ConflictInfo<T>,
      }
    }
  }

  async list(options: ListOptions = {}): Promise<Result<StorageMetadata[]>> {
    try {
      const headers = await this.httpClient.getAuthHeaders()

      // Build query string
      const params = new URLSearchParams()
      if (options.orgId) params.append('orgId', options.orgId)
      if (options.userId) params.append('userId', options.userId)
      if (options.limit) params.append('limit', options.limit.toString())
      if (options.offset) params.append('offset', options.offset.toString())
      if (options.sortBy) params.append('sortBy', options.sortBy)
      if (options.sortDirection) params.append('sortDirection', options.sortDirection)

      const queryString = params.toString()
      const baseUrl = this.httpClient.getBaseUrl()
      const url = `${baseUrl}/storage/${this.storageType}${queryString ? `?${queryString}` : ''}`

      const response = await this.httpClient.fetch<
        StorageMetadata[] | { items: StorageMetadata[] }
      >(url, { method: 'GET', headers })

      // Handle both array and {items: array} response formats
      const items = Array.isArray(response) ? response : response.items
      return { success: true, data: items }
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof StorageError
            ? error
            : new StorageError('Failed to list items', StorageErrorCode.UNKNOWN, error),
      }
    }
  }

  async exists(key: string): Promise<Result<boolean>> {
    const result = await this.get(key)

    if (!result.success) {
      return { success: false, error: result.error }
    }

    return { success: true, data: result.data !== null }
  }

  async clear(options: { orgId?: string } = {}): Promise<Result<void>> {
    try {
      const headers = await this.httpClient.getAuthHeaders()

      const params = new URLSearchParams()
      if (options.orgId) params.append('orgId', options.orgId)

      const queryString = params.toString()
      const baseUrl = this.httpClient.getBaseUrl()
      const url = `${baseUrl}/storage/${this.storageType}${queryString ? `?${queryString}` : ''}`

      await this.httpClient.fetch<void>(url, { method: 'DELETE', headers })

      return { success: true, data: undefined }
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof StorageError
            ? error
            : new StorageError('Failed to clear storage', StorageErrorCode.UNKNOWN, error),
      }
    }
  }

  async isAvailable(): Promise<boolean> {
    try {
      // Try to get auth token
      const headers = await this.httpClient.getAuthHeaders()

      // Try a simple health check request
      const baseUrl = this.httpClient.getBaseUrl()
      const response = await fetch(`${baseUrl}/health`, {
        method: 'GET',
        headers,
      })

      return response.ok
    } catch {
      return false
    }
  }
}
